import React, { useEffect, useMemo, useRef, useState } from "react";

import { IconButton, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { zonedTimeToUtc, utcToZonedTime, format } from "date-fns-tz";
import { getUserDateTimeFormatPattern } from "Util/dateFormaterPreference";
import { useOperationDespatchContext } from "../../../../../../../../contexts/DespatchContext";
import { useDespatchActions } from "../../../../hooks/useDespatchActions";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "Constants";
import { useAppState } from "Context/StateContext";
import {
  Maximize as IconMaximize,
  Clock as IconClock,
  MinusCirlce as IconMinusCircle,
  CloseCircle as IconCloseCircle,
  ArrowUp2 as IconArrowUp,
  AddCircle as IconAddCircle,
  Trash as DeleteIcon,
  InfoCircle as InfoIcon,
} from "iconsax-react";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { useTranslation } from "react-i18next";
import { checkAvailability, checkResourceAvailability } from "Util/checkAvailability";
import { EquipmentType, ResourceName, ResourceSubGroup, TaskStatus } from "Util/enums";

import {
  getPublishOrUnpublishOption,
  isCrewEnabledToRemoveLeader,
  isTaskEnabledToCancel,
  isTaskEnabledToRemoveFromTeam,
  isCrewTaskEnableToPublish,
  isTaskEnableToUnpublish,
  isCrewEnabledToEditResourcesAndLeader,
} from "Util/operation.despatch";
import { operationsPermissions } from "Util/permissions";

import { AppMenu, AppMenuProps } from "../../../../../../../../../../components/AppMenu";
import { ResourceMenuHandles } from "../../../../../../../../../../components/AppMenu/AppMenu";
import { ResourceOrderCard } from "../../../ResourceList/components/ResourceOrderCard";
import { ResourceSlot, ResourceSlotSubItem } from "../../../ResourceSlot";
import { DNDSlotTypes, generateSlotSourceId, DNDFieldsNames } from "../../useDragSystem";
import { IconTeam } from "Components/Generic/Icons/IconTeam";
import { IconCustomTeam } from "Components/Generic/Icons/IconCustomTeam";

import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  StatusLabel,
  StorageBoxSlotSingleUseAdd,
  Title,
  UnpublishTaskIcon,
  PublishTaskIcon,
  ResourceWrapper,
  TeamExtraInfo,
  FooterInfoIcon,
} from "./styled";
import { TimeSlotTask } from "Models/time.slot.task";
import TeamsService from "Services/teams.service";

import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { createDragPreview } from "../../useDragSystem/DragPreview";
import differenceInDays from "date-fns/differenceInDays";
import { compareText } from "Util/resourceSorting";
import { StorageBox } from "Models/storage.box";

interface TeamCardProps {
  crew: DespatchCrew;
  onRemoveElement: (removedId: string, id?: number) => void;
  onCloseCrew: (crew: DespatchCrew) => void;
}

const DraggableTaskItem = ({
  task,
  index,
  isUserEnableToEdit,
  openMenu,
  parentTeamId,
}: {
  task: DespatchTask;
  index: number;
  isUserEnableToEdit: boolean;
  openMenu: (element: HTMLElement, task: DespatchTask) => void;
  parentTeamId: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { setDraggedItemHeight } = useOperationDespatchContext();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const canDrag = isUserEnableToEdit && task.status === TaskStatus.REGISTERED;
    if (!canDrag) return;

    return draggable({
      element,
      getInitialData: () => ({
        task,
        index: 10000 + index,
        type: DNDFieldsNames.LIST_ORDERS,
        slotType: DNDSlotTypes.CREW_ORDERS,
        parentTeamId,
        sourceId: generateSlotSourceId(
          task.id,
          "source",
          DNDFieldsNames.LIST_ORDERS,
          DNDSlotTypes.CREW_ORDERS
        ),
      }),
      onGenerateDragPreview: createDragPreview(element),
      onDragStart: () => {
        const rect = element.getBoundingClientRect();
        setDraggedItemHeight(rect.height);

        setIsDragging(true);
      },
      onDrop: () => setIsDragging(false),
    });
  }, [task, index, isUserEnableToEdit]);

  return (
    <ResourceWrapper
      isDragging={isDragging}
      cursor={isUserEnableToEdit && task.status === TaskStatus.REGISTERED ? "grab" : "default"}
      ref={ref}
    >
      <ResourceOrderCard
        disabledTime={!isUserEnableToEdit}
        rounded
        fullWidth
        task={task}
        onMoreActionsClick={(event) => openMenu(event.currentTarget, task)}
      />
    </ResourceWrapper>
  );
};

export const TeamCard = (props: TeamCardProps) => {
  const { currentUser, formatWithUserTimezone, userTimezone } = useAppState();
  const { crew, onRemoveElement, onCloseCrew } = props;
  const [isRemoving, setIsRemoving] = useState(false);

  const generatorId = (slotName: DNDSlotTypes) => {
    return generateSlotSourceId(crew.id, "slot", DNDFieldsNames.CARD_TEAM, slotName);
  };

  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const menuRef = useRef<ResourceMenuHandles | null>(null);

  const selectedTask = useMemo(() => {
    if (!selectedTaskId) return;

    return crew.tasks.find((task) => task.id === selectedTaskId);
  }, [crew, selectedTaskId]);

  const { t: translate } = useTranslation();

  const {
    updateCrewTaskValueByTaskId,
    expandedTeams,
    toggleTeamExpanded,
    currentDraggable,
    currentDate,
    despatchTimeControlRef,
    cancelTaskRef,
    deletePlaceholderTaskRef,
    storageBoxSingleUseRef,
    changesMade,
    persistCrews,
  } = useOperationDespatchContext();

  const isUserEnableToEdit = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.edit;

  const isUserAccessToEditOrders = operationsPermissions({ currentUser })?.edit;

  const { usePublishTask, useUnpublishTask } = useDespatchActions();

  const onSuccessPublishingTask = (task: DespatchTask, taskStatus: string) => {
    updateCrewTaskValueByTaskId(task.id, "status", taskStatus);
  };
  const { mutate: publishTask, isLoading: isLoadingPublish } = usePublishTask((task) =>
    onSuccessPublishingTask(task, TaskStatus.PENDING_ACCEPT)
  );
  const { mutate: unpublishTask, isLoading: isLoadingUnpublish } = useUnpublishTask((task) =>
    onSuccessPublishingTask(task, TaskStatus.REGISTERED)
  );

  const { data: rootTeam } = useQuery({
    queryKey: ["find-team-by-id", crew.rootTeamId],
    queryFn: async () => {
      const response = await TeamsService.getById(Number(crew.rootTeamId));
      return response.data;
    },
    enabled: !!crew.rootTeamId && crew.rootTeamId !== crew.id,
  });

  const managePublishing = (task: DespatchTask, crew: DespatchCrew) => {
    if (isCrewTaskEnableToPublish(task, crew)) {
      if (!crew.memberLeader) {
        NotificationToast.warning(translate("youCannotPublishATaskWithoutALeader"));

        return;
      }
      publishTask({
        taskId: task.id,
        crew,
        task,
      });
    } else if (isTaskEnableToUnpublish(task)) {
      unpublishTask({
        taskId: task.id,
        crew,
        task,
      });
    }
  };
  const isLoadingPublishing = isLoadingPublish || isLoadingUnpublish;

  const findBoardDayTimeSlot = (timeSlots: TimeSlotTask[]) => {
    const timezone = userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const boardZoned = utcToZonedTime(currentDate, timezone);

    return (
      timeSlots.find((value) => {
        const slotZoned = utcToZonedTime(value.date, timezone);
        return (
          slotZoned.getFullYear() === boardZoned.getFullYear() &&
          slotZoned.getMonth() === boardZoned.getMonth() &&
          slotZoned.getDate() === boardZoned.getDate()
        );
      }) ?? null
    );
  };

  /** Board date + wall time for sort. Slot `from` is already wall clock; UTC startDate uses user TZ. */
  const getTaskSortDate = (task: DespatchTask) => {
    const boardDate = formatWithUserTimezone(currentDate, "yyyy-MM-dd");

    if (task.timeSlots?.length) {
      const matchingSlot = findBoardDayTimeSlot(task.timeSlots);
      if (matchingSlot?.from) {
        const from =
          matchingSlot.from.length === 5 ? `${matchingSlot.from}:00` : matchingSlot.from;
        return new Date(`${boardDate} ${from}`);
      }
    }

    const timeInUserTz = formatWithUserTimezone(task.startDate, "HH:mm:ss");
    return new Date(`${boardDate} ${timeInUserTz}`);
  };

  const crewTasksSorted = useMemo(() => {
    return [...crew.tasks].sort((taskA, taskB) => {
      return getTaskSortDate(taskA).getTime() - getTaskSortDate(taskB).getTime();
    });
  }, [crew?.tasks, crew?.id, currentDate, userTimezone?.name]);

  const getTooltipMessageIfTryToRemoveTaskFromTeam = (task: DespatchTask) => {
    switch (task.status as TaskStatus) {
      case TaskStatus.PENDING_ACCEPT:
        return translate("youHaveToUnpublishFirst");
      case TaskStatus.ACCEPTED:
        return translate("youHaveToUnpublishFirst");
      case TaskStatus.STARTED:
        return translate("thisTaskAlreadyStartedByTheLeader");
      case TaskStatus.FINISHED:
        return translate("thisTaskAlreadyFinished");
      case TaskStatus.CANCELLED:
        return translate("thisTaskAlreadyCancelled");
      default:
        return "";
    }
  };

  const tooltipBlockedMessageSBSingleUse = useMemo(() => {
    const tasks = crew.tasks || [];

    if (!tasks.length) {
      return translate("youHaveToAddATaskBeforeCreateASingleUse");
    }

    if (tasks.length > 1) {
      const allJobIds = tasks.map((task) => task.job.id);
      let allIsSame = true;

      for (const jobId of allJobIds) {
        if (allIsSame) {
          allIsSame = allJobIds.every((compare) => compare === jobId);
        }

        if (!allIsSame) {
          break;
        }
      }

      if (!allIsSame) {
        return translate("youCannotAddASingleUseForDifferentOrders");
      }
    }

    return "";
  }, [crew]);

  const isCrewLeaderUnavailable = useMemo(
    () =>
      !checkAvailability(currentDate, crew?.memberLeader?.availabilities || [], userTimezone?.name)
        .isAvailable,
    [crew]
  );

  const isInDraft = useMemo(() => isCrewEnabledToRemoveLeader(crew), [crew]);

  const isCrewEnabledToEditResources = useMemo(() => {
    return isCrewEnabledToEditResourcesAndLeader(crew, currentDate);
  }, [crew]);

  const [isAccordionOpened, setIsAccordionOpened] = useState(false);
  const isExpanded = useMemo(() => {
    return expandedTeams.includes(crew.id);
  }, [expandedTeams]);

  useEffect(() => {
    setIsAccordionOpened(isExpanded);
  }, [isExpanded]);

  const taskOptions: AppMenuProps["actions"] = useMemo(() => {
    const jobId = selectedTask?.job?.id;

    if (!selectedTask) return [];

    return [
      {
        label: translate("openOrder"),
        leftContent: <IconMaximize size={24} />,
        onClick: () => {
          const url = `${PRIVATE_ROUTES.ORDERS_MNT}/${jobId}`;

          window.open(url);
        },
        hidden: !jobId || (!isUserEnableToEdit && !isUserAccessToEditOrders),
      },
      // {
      //   label: translate("accessDetails"),
      //   leftContent: <IconMap size={24} />,
      //   onClick: () => {},
      // },
      // {
      //   label: translate("generateReport"),
      //   leftContent: <IconPrinter size={24} />,
      //   onClick: () => {},
      // },
      {
        isDivider: true,
        hidden: !jobId || (!isUserEnableToEdit && !isUserAccessToEditOrders),
      },

      {
        ...getPublishOrUnpublishOption(
          selectedTask,
          crew,
          translate,
          PublishTaskIcon,
          UnpublishTaskIcon,
          () => {
            if (changesMade?.length) {
              persistCrews(() => {
                managePublishing(selectedTask, crew);
              });
            } else {
              managePublishing(selectedTask, crew);
            }
          },
          isLoadingPublishing
        ),
        hidden: selectedTask?.isPlaceholder || !isUserEnableToEdit,
      },
      {
        label: translate("timeControl"),
        leftContent: <IconClock size={24} />,
        onClick: () => {
          despatchTimeControlRef?.current?.open(selectedTask?.id, isUserEnableToEdit);
        },
        hidden: selectedTask?.isPlaceholder,
      },
      {
        isDivider: true,
        hidden: selectedTask?.isPlaceholder,
      },
      {
        label: translate("removeTaskFromTeam"),
        leftContent: <IconMinusCircle size={24} />,
        isDisabled: !isTaskEnabledToRemoveFromTeam(selectedTask) || !isUserEnableToEdit,
        onClick: async () => {
          if (isRemoving) return;

          setIsRemoving(true);

          try {
            onRemoveElement(generatorId(DNDSlotTypes.CREW_ORDERS), selectedTask.id);
            setSelectedTaskId(null);
            menuRef.current?.close();
          } finally {
            // Add a small delay to prevent rapid clicking
            setTimeout(() => setIsRemoving(false), 500);
          }
        },
        tooltipMessage: !isTaskEnabledToRemoveFromTeam(selectedTask)
          ? getTooltipMessageIfTryToRemoveTaskFromTeam(selectedTask)
          : undefined,
        hidden: !isUserEnableToEdit,
      },
      {
        label: translate("cancelTask"),
        leftContent: <IconCloseCircle size={24} />,
        onClick: () => {
          if (changesMade?.length) {
            persistCrews(() => {
              cancelTaskRef.current?.open(selectedTask, crew);
            });
          } else {
            cancelTaskRef.current?.open(selectedTask, crew);
          }
        },
        isError: true,
        isDisabled: !isTaskEnabledToCancel(selectedTask),
        tooltipMessage: !isTaskEnabledToCancel(selectedTask)
          ? translate("thisTaskCannotBeCancelled")
          : undefined,
        hidden: selectedTask?.isPlaceholder || !isUserEnableToEdit,
      },
      {
        label: translate("deleteTask"),
        leftContent: <DeleteIcon size={24} />,
        onClick: () => {
          if (changesMade?.length) {
            persistCrews(() => {
              deletePlaceholderTaskRef.current?.open(selectedTask, crew);
            });
          } else {
            deletePlaceholderTaskRef.current?.open(selectedTask, crew);
          }
        },
        isError: true,
        tooltipMessage: !isTaskEnabledToCancel(selectedTask)
          ? translate("thisTaskCannotBeCancelled")
          : undefined,
        hidden: !selectedTask?.isPlaceholder || !isUserEnableToEdit,
      },
    ].filter((action) => !action.hidden) as AppMenuProps["actions"];
  }, [selectedTask, crew, isLoadingPublishing]);

  // Add this useEffect after the existing useEffect
  useEffect(() => {
    // If selectedTaskId exists but the task is no longer in the crew, reset it
    if (selectedTaskId && !crew.tasks.find((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(null);
      menuRef.current?.close();
    }
  }, [crew.tasks, selectedTaskId]);

  const openMenu = (element: HTMLElement, task: DespatchTask) => {
    setSelectedTaskId(task.id);
    menuRef.current?.open(element);
  };

  const isTeamACrewForThatDay = useMemo(() => {
    if (crew.isAutoCreated) return true;

    if (crew.isCrew && (!crew.rootTeamId || crew.rootTeamId === crew.id)) return true;

    if (crew.isCrew && crew.rootTeamId !== crew.id) {
      if (!rootTeam) return false;
      return rootTeam.isCrew;
    }

    return false;
  }, [crew, rootTeam]);

  const sortedMembers = useMemo(() => {
    const members = crew?.members ?? [];
    return [...members].sort((a, b) => compareText(a.fullName ?? "", b.fullName ?? ""));
  }, [crew?.members]);

  const getEquipmentsSort = (equipment: any) => {
    return equipment.equipmentType === EquipmentType.GENERAL
      ? equipment.description ?? ""
      : String(equipment.fleetNumber ?? "");
  };

  const sortedFleets = useMemo(() => {
    const fleets = crew.fleets ?? [];
    return [...fleets].sort((a, b) => compareText(getEquipmentsSort(a), getEquipmentsSort(b)));
  }, [crew.fleets]);

  const sortedStorageBoxes = useMemo(() => {
    const storageBoxes = crew.storageBoxes ?? [];

    const getSortFullTextKey = (box: Pick<StorageBox, "number" | "storageBoxType">) => {
      const typeName = box.storageBoxType?.name?.trim() ?? "";
      const number = String(box.number ?? "").trim();
      return `${typeName} #${number}`.trim();
    };

    return [...storageBoxes].sort((a, b) =>
      compareText(getSortFullTextKey(a), getSortFullTextKey(b))
    );
  }, [crew.storageBoxes]);

  const renderResources = () => {
    return (
      <Accordion
        elevation={0}
        expanded={isAccordionOpened}
        onChange={(_event, expanded) => {
          setIsAccordionOpened(expanded);
        }}
      >
        <AccordionSummary
          onClick={() => toggleTeamExpanded(crew.id)}
          expandIcon={<IconArrowUp size={20} />}
        >
          <Title>{crew.name}</Title>
          {isInDraft && <StatusLabel>{translate("draft")}</StatusLabel>}
        </AccordionSummary>
        <AccordionDetails>
          <ResourceSlot
            isDropDisabled={!isAccordionOpened}
            title={
              <ResourceSlotSubItem
                hasDeleteButton={isUserEnableToEdit}
                deleteButtonProps={{
                  disabled: !isCrewEnabledToEditResources,
                  disabledTooltip: translate("thisTeamHasTasksAlreadyPublishedForThisLeader"),
                }}
                disableHouver={!!currentDraggable}
                onDeleteClick={() => onRemoveElement(generatorId(DNDSlotTypes.CREW_LEADER))}
                text={crew.memberLeader?.fullName}
                hasError={isCrewLeaderUnavailable}
                hasTooltip={isCrewLeaderUnavailable}
                tooltipText={translate("thisMemberIsUnavailableOnThisDate")}
              />
            }
            id={generatorId(DNDSlotTypes.CREW_LEADER)}
            iconName="crown"
            active={!!crew?.memberLeader}
            placeHolder={translate("noLeaderAdded")}
          />
          <ResourceSlot
            isDropDisabled={!isAccordionOpened}
            title={sortedMembers.map((member) => {
              const hasError = !checkAvailability(
                currentDate,
                member.availabilities,
                userTimezone.name
              ).isAvailable;

              const memberIsNoLongerAvailable =
                !!member.endEmploymentDate &&
                differenceInDays(currentDate, new Date(member.endEmploymentDate)) > 0;

              return (
                <ResourceSlotSubItem
                  key={member.id}
                  hasDeleteButton={isUserEnableToEdit}
                  disableHouver={!!currentDraggable}
                  onDeleteClick={() =>
                    onRemoveElement(generatorId(DNDSlotTypes.CREW_STAFFS), member.id)
                  }
                  text={member.fullName}
                  hasError={hasError || memberIsNoLongerAvailable}
                  hasTooltip={hasError || memberIsNoLongerAvailable}
                  tooltipText={translate("thisMemberIsUnavailableOnThisDate")}
                />
              );
            })}
            iconName="people"
            id={generatorId(DNDSlotTypes.CREW_STAFFS)}
            active={!!sortedMembers.length}
            placeHolder={translate("noStaffAdded")}
          />
          <ResourceSlot
            isDropDisabled={!isAccordionOpened}
            title={sortedFleets.map((equipment) => {
              const hasError = !checkResourceAvailability(
                currentDate,
                equipment.availabilities,
                userTimezone.name
              ).isAvailable;

              const equipmentName =
                equipment.equipmentType === EquipmentType.GENERAL
                  ? equipment.description
                  : `#${equipment.fleetNumber}`;

              return (
                <ResourceSlotSubItem
                  key={equipment.id}
                  hasDeleteButton={isUserEnableToEdit}
                  disableHouver={!!currentDraggable}
                  onDeleteClick={() =>
                    onRemoveElement(generatorId(DNDSlotTypes.CREW_EQUIPMENTS), equipment.id)
                  }
                  hasError={hasError}
                  hasTooltip={hasError}
                  tooltipText={translate("thisFleetIsUnavailableOnThisDate")}
                  title={equipment.equipmentType}
                  text={equipmentName}
                />
              );
            })}
            iconName="truck"
            id={generatorId(DNDSlotTypes.CREW_EQUIPMENTS)}
            active={!!sortedFleets.length}
            placeHolder={translate("noEquipmentAdded")}
          />
          <ResourceSlot
            isDropDisabled={!isAccordionOpened}
            title={sortedStorageBoxes.map((storageBox) => (
              <ResourceSlotSubItem
                key={storageBox.id}
                hasDeleteButton={isUserEnableToEdit}
                disableHouver={!!currentDraggable}
                onDeleteClick={() =>
                  onRemoveElement(generatorId(DNDSlotTypes.CREW_STORAGE_BOXES), storageBox.id)
                }
                title={storageBox.storageBoxType?.name}
                text={`#${storageBox.number}`}
              />
            ))}
            iconName="container"
            id={generatorId(DNDSlotTypes.CREW_STORAGE_BOXES)}
            active={!!sortedStorageBoxes?.length}
            placeHolder={translate("noStorageBoxAdded")}
            renderAtRight={
              isUserEnableToEdit ? (
                <StorageBoxSlotSingleUseAdd>
                  <Tooltip
                    title={
                      tooltipBlockedMessageSBSingleUse || translate("createAStorageBoxSingleUse")
                    }
                  >
                    <div>
                      <IconButton
                        onClick={() =>
                          storageBoxSingleUseRef.current?.open(
                            {
                              id: crew.tasks?.[0]?.job?.id,
                              reference: crew.tasks?.[0]?.job?.reference,
                            },
                            undefined,
                            crew
                          )
                        }
                        disabled={!!tooltipBlockedMessageSBSingleUse}
                      >
                        <IconAddCircle
                          size={20}
                          color={
                            tooltipBlockedMessageSBSingleUse ? colors.gray600 : colors.brandBlue500
                          }
                        />
                      </IconButton>
                    </div>
                  </Tooltip>
                </StorageBoxSlotSingleUseAdd>
              ) : null
            }
          />
          <ResourceSlot
            id={generatorId(DNDSlotTypes.CREW_ORDERS)}
            isDropDisabled={!isAccordionOpened}
            iconName={crewTasksSorted.length ? undefined : "clipboard-text"}
            disableGutters={!!crewTasksSorted.length}
            backgroundColor={crewTasksSorted.length ? "transparent" : undefined}
            title={
              <>
                {crewTasksSorted.map((task, index) => (
                  <DraggableTaskItem
                    key={task.id}
                    task={task}
                    index={index}
                    isUserEnableToEdit={isUserEnableToEdit}
                    openMenu={openMenu}
                    parentTeamId={crew.id}
                  />
                ))}
              </>
            }
            active={!!crewTasksSorted.length}
            placeHolder={translate("noTaskAdded")}
          />
          <AppMenu actions={taskOptions} ref={menuRef} />
        </AccordionDetails>
        <AccordionActions>
          <div>
            {isUserEnableToEdit && !!crew.branches?.length && (
              <Tooltip
                title={
                  <TeamExtraInfo>
                    <span>
                      <b>{translate("teamBranches")}:</b>{" "}
                      {crew.branches?.map((branch) => branch.name).join(", ")}
                    </span>
                    {!!crew?.timezoneId && (
                      <>
                        <span>
                          <b>{translate("timezone")}</b>: {crew?.timezoneName}
                        </span>
                        <span>
                          {format(
                            zonedTimeToUtc(crew.createdTo!, crew.timezoneName!),
                            getUserDateTimeFormatPattern(currentUser?.preferences)
                          )}{" "}
                          (UTC)
                        </span>
                      </>
                    )}
                  </TeamExtraInfo>
                }
              >
                <FooterInfoIcon>
                  <InfoIcon size={24} color={colors.gray400} />
                </FooterInfoIcon>
              </Tooltip>
            )}
            <div style={{ marginRight: 8 }}>
              {isTeamACrewForThatDay ? (
                <Tooltip title={translate("customTeam")}>
                  <div>
                    <IconCustomTeam width={26} height={26} color={colors.gray400} />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip title={translate("defaultTeam")}>
                  <div>
                    <IconTeam width={26} height={26} color={colors.gray400} />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          {isUserEnableToEdit && (
            <Tooltip
              title={
                isInDraft
                  ? translate("removeThisTeamFromTheBoard")
                  : translate("teamRemovalIsBlockedDueToThePresenceOfTasks")
              }
            >
              <div>
                <IconButton onClick={() => onCloseCrew(crew)} disabled={!isInDraft}>
                  <IconCloseCircle size={24} color={isInDraft ? colors.error500 : colors.gray400} />
                </IconButton>
              </div>
            </Tooltip>
          )}
        </AccordionActions>
      </Accordion>
    );
  };

  return renderResources();
};
